import { useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  Play, ExternalLink, X, Maximize2, Volume2,
  Music, Film, Link2, RefreshCw, Loader2
} from 'lucide-react';
import { cn } from '../lib/utils';
import { API } from '../config/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

// Provider brand colors
const PROVIDER_COLORS = {
  youtube: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', accent: '#FF0000' },
  spotify: { bg: 'bg-green-500/10', border: 'border-green-500/30', text: 'text-green-400', accent: '#1DB954' },
  apple_music: { bg: 'bg-pink-500/10', border: 'border-pink-500/30', text: 'text-pink-400', accent: '#FC3C44' },
  soundcloud: { bg: 'bg-orange-500/10', border: 'border-orange-500/30', text: 'text-orange-400', accent: '#FF5500' },
  audiomack: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', accent: '#FFA200' },
  bandcamp: { bg: 'bg-cyan-500/10', border: 'border-cyan-500/30', text: 'text-cyan-400', accent: '#1DA0C3' },
  tidal: { bg: 'bg-slate-500/10', border: 'border-slate-500/30', text: 'text-slate-300', accent: '#000000' },
  amazon_music: { bg: 'bg-blue-500/10', border: 'border-blue-500/30', text: 'text-blue-400', accent: '#00A8E1' },
  generic: { bg: 'bg-muted/30', border: 'border-border/30', text: 'text-muted-foreground', accent: '#71717A' },
};

const ProviderIcon = ({ provider, size = 16 }) => {
  const icons = {
    youtube: <Film style={{ width: size, height: size, color: '#FF0000' }} />,
    spotify: <Music style={{ width: size, height: size, color: '#1DB954' }} />,
    apple_music: <Music style={{ width: size, height: size, color: '#FC3C44' }} />,
    soundcloud: <Volume2 style={{ width: size, height: size, color: '#FF5500' }} />,
    audiomack: <Music style={{ width: size, height: size, color: '#FFA200' }} />,
    bandcamp: <Music style={{ width: size, height: size, color: '#1DA0C3' }} />,
    tidal: <Music style={{ width: size, height: size, color: '#000' }} />,
    amazon_music: <Music style={{ width: size, height: size, color: '#00A8E1' }} />,
    generic: <Link2 style={{ width: size, height: size }} />,
  };
  return icons[provider] || icons.generic;
};

// ===================== EMBED MODAL =====================

function EmbedModal({ embed, isOpen, onClose }) {
  if (!isOpen || !embed) return null;

  const provider = embed.provider || 'generic';
  const colors = PROVIDER_COLORS[provider] || PROVIDER_COLORS.generic;
  const isVideo = embed.content_type === 'video';

  // Determine iframe src
  let iframeSrc = embed.embed_url;

  // For SoundCloud, extract src from embed HTML
  if (provider === 'soundcloud' && embed.embed_html && !iframeSrc) {
    const match = embed.embed_html.match(/src="([^"]+)"/);
    if (match) iframeSrc = match[1];
  }

  return (
    <div 
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="embed-modal-overlay"
    >
      <div className={cn(
        "relative w-full max-w-3xl rounded-xl overflow-hidden border shadow-2xl",
        "bg-background", colors.border
      )}>
        {/* Modal Header */}
        <div className="flex items-center justify-between p-3 border-b border-border">
          <div className="flex items-center gap-2 min-w-0">
            <ProviderIcon provider={provider} size={18} />
            <span className={cn("text-sm font-medium truncate", colors.text)}>
              {embed.provider_name || provider}
            </span>
            <span className="text-xs text-muted-foreground truncate max-w-[200px]">
              {embed.title}
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <a
              href={embed.original_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex"
              data-testid="embed-open-original"
            >
              <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground">
                <ExternalLink className="w-4 h-4" />
              </Button>
            </a>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8 text-muted-foreground hover:text-foreground"
              onClick={onClose}
              data-testid="embed-modal-close"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Embed Content */}
        <div className={cn(
          "w-full",
          isVideo ? "aspect-video" : "h-[352px]"
        )}>
          {iframeSrc ? (
            <iframe
              src={iframeSrc}
              className="w-full h-full border-0"
              allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
              allowFullScreen
              loading="lazy"
              title={embed.title || 'Embedded content'}
              sandbox="allow-scripts allow-same-origin allow-popups allow-presentation"
              data-testid="embed-iframe"
            />
          ) : embed.embed_html ? (
            <div 
              className="w-full h-full"
              dangerouslySetInnerHTML={{ __html: embed.embed_html }}
            />
          ) : (
            // Fallback: rich card with link
            <div className="flex flex-col items-center justify-center h-full p-8 text-center gap-4">
              {embed.thumbnail_url && (
                <img 
                  src={embed.thumbnail_url} 
                  alt={embed.title}
                  className="w-48 h-48 object-cover rounded-lg"
                />
              )}
              <h3 className="text-lg font-semibold">{embed.title}</h3>
              {embed.description && (
                <p className="text-sm text-muted-foreground line-clamp-3 max-w-md">
                  {embed.description}
                </p>
              )}
              <a
                href={embed.original_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="outline" className="gap-2">
                  <ExternalLink className="w-4 h-4" />
                  Open on {embed.provider_name || 'Website'}
                </Button>
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ===================== LINK EMBED CARD =====================

export function LinkEmbedCard({ embed, postId, isOwner, isAdmin, compact = false }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const { getAuthHeaders } = useAuth();

  if (!embed) return null;

  const provider = embed.provider || 'generic';
  const colors = PROVIDER_COLORS[provider] || PROVIDER_COLORS.generic;
  const hasEmbed = embed.embed_url || embed.embed_html;

  const handleRefresh = async (e) => {
    e.stopPropagation();
    if (!postId) return;
    setRefreshing(true);
    try {
      const res = await fetch(`${API}/embeds/refresh/${postId}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        toast.success('Link metadata refreshed');
        window.location.reload();
      } else {
        toast.error('Failed to refresh metadata');
      }
    } catch {
      toast.error('Failed to refresh metadata');
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <>
      <div
        className={cn(
          "rounded-lg border overflow-hidden cursor-pointer transition-all",
          "hover:border-border hover:shadow-lg group",
          colors.bg, colors.border,
          compact ? "flex items-center gap-3 p-2" : ""
        )}
        onClick={() => setModalOpen(true)}
        data-testid="link-embed-card"
      >
        {/* Thumbnail */}
        {embed.thumbnail_url && !compact && (
          <div className="relative aspect-video bg-black/40 overflow-hidden">
            <img
              src={embed.thumbnail_url}
              alt={embed.title || ''}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              loading="lazy"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            {/* Play overlay */}
            {hasEmbed && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                <div className={cn(
                  "w-14 h-14 rounded-full flex items-center justify-center",
                  "bg-white/90 shadow-xl group-hover:scale-110 transition-transform"
                )}>
                  <Play className="w-6 h-6 text-black ml-0.5" fill="black" />
                </div>
              </div>
            )}
            {/* Provider badge */}
            <Badge 
              className={cn(
                "absolute top-2 left-2 text-xs gap-1",
                colors.bg, colors.text, "border-0 backdrop-blur-sm"
              )}
            >
              <ProviderIcon provider={provider} size={12} />
              {embed.provider_name}
            </Badge>
            {/* Duration */}
            {embed.duration && (
              <Badge className="absolute bottom-2 right-2 text-xs bg-background/70 border-0">
                {formatDuration(embed.duration)}
              </Badge>
            )}
          </div>
        )}

        {/* Compact thumbnail */}
        {embed.thumbnail_url && compact && (
          <div className="relative w-16 h-16 shrink-0 rounded overflow-hidden bg-black/40">
            <img
              src={embed.thumbnail_url}
              alt=""
              className="w-full h-full object-cover"
              loading="lazy"
            />
            {hasEmbed && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <Play className="w-5 h-5 text-white" fill="white" />
              </div>
            )}
          </div>
        )}

        {/* Info section */}
        <div className={cn("p-3", compact && "flex-1 min-w-0 p-0")}>
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              {compact && (
                <div className="flex items-center gap-1 mb-0.5">
                  <ProviderIcon provider={provider} size={12} />
                  <span className={cn("text-xs", colors.text)}>{embed.provider_name}</span>
                </div>
              )}
              <h4 className={cn(
                "font-medium line-clamp-2 leading-snug",
                compact ? "text-sm" : "text-sm"
              )}>
                {embed.title}
              </h4>
              {!compact && embed.description && (
                <p className="text-xs text-muted-foreground line-clamp-2 mt-1">
                  {embed.description}
                </p>
              )}
              {!compact && embed.author_name && (
                <p className="text-xs text-muted-foreground mt-1">
                  {embed.author_name}
                </p>
              )}
            </div>
            {/* Actions */}
            <div className="flex items-center gap-1 shrink-0">
              {(isOwner || isAdmin) && postId && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={handleRefresh}
                  disabled={refreshing}
                  data-testid="link-embed-refresh"
                >
                  {refreshing ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3 h-3" />
                  )}
                </Button>
              )}
              {hasEmbed && !compact && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={(e) => { e.stopPropagation(); setModalOpen(true); }}
                  data-testid="link-embed-expand"
                >
                  <Maximize2 className="w-3 h-3" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Modal Player */}
      <EmbedModal
        embed={embed}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
}

// ===================== LINK URL INPUT (for PostComposer) =====================

export function LinkUrlInput({ value, onChange, onResolve, resolving, resolvedEmbed, onClear }) {
  const [inputValue, setInputValue] = useState(value || '');

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData('text');
    if (pasted && /^https?:\/\//i.test(pasted.trim())) {
      setInputValue(pasted.trim());
      onChange(pasted.trim());
      onResolve(pasted.trim());
    }
  };

  const handleSubmit = () => {
    const url = inputValue.trim();
    if (url) {
      onChange(url);
      onResolve(url);
    }
  };

  if (resolvedEmbed) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground flex items-center gap-1">
            <Link2 className="w-3 h-3" /> Link Preview
          </span>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 text-xs"
            onClick={onClear}
          >
            <X className="w-3 h-3 mr-1" /> Remove
          </Button>
        </div>
        <LinkEmbedCard embed={resolvedEmbed} compact />
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <div className="relative flex-1">
        <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="url"
          placeholder="Paste a YouTube, Spotify, Apple Music link..."
          value={inputValue}
          onChange={(e) => { setInputValue(e.target.value); onChange(e.target.value); }}
          onPaste={handlePaste}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSubmit(); } }}
          className={cn(
            "w-full pl-9 pr-3 py-2 rounded-lg text-sm",
            "bg-card/50 border border-border focus:border-border",
            "outline-none transition-colors placeholder:text-muted-foreground/50"
          )}
          data-testid="link-url-input"
        />
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={handleSubmit}
        disabled={!inputValue.trim() || resolving}
        data-testid="link-resolve-button"
      >
        {resolving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Preview'}
      </Button>
    </div>
  );
}

function formatDuration(seconds) {
  if (!seconds) return '';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

export default LinkEmbedCard;
